import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AUTH_STORAGE_KEY, setUnauthorizedHandler } from '../services/apiClient';
import { loginWithPassword, verifyOtp } from '../services/authService';
import { getStudentProfile } from '../services/studentService';

const AuthContext = createContext(null);

function readStoredAuth() {
  try {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);

    return storedAuth ? JSON.parse(storedAuth) : null;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => readStoredAuth());

  function persistAuth(authData) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
    setAuth(authData);
  }

  const signInWithPassword = useCallback(async (credentials) => {
    const authData = await loginWithPassword(credentials);
    persistAuth(authData);

    return authData;
  }, []);

  const signInWithOtp = useCallback(async (payload) => {
    const authData = await verifyOtp(payload);
    persistAuth(authData);

    return authData;
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuth(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuth(null);
      window.location.replace('/login');
    });

    return () => {
      setUnauthorizedHandler(null);
    };
  }, []);

  useEffect(() => {
    if (!auth?.accessToken) {
      return undefined;
    }

    let isMounted = true;

    async function loadStudentProfile() {
      try {
        const profile = await getStudentProfile();

        if (!isMounted) {
          return;
        }

        setAuth((currentAuth) => {
          if (!currentAuth?.accessToken) {
            return currentAuth;
          }

          const updatedAuth = { ...currentAuth, user: profile };
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updatedAuth));

          return updatedAuth;
        });
      } catch {
        // Keep the user data from login if profile fetch fails.
      }
    }

    loadStudentProfile();

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken]);

  const value = useMemo(
    () => ({
      accessToken: auth?.accessToken || null,
      isAuthenticated: Boolean(auth?.accessToken),
      signInWithOtp,
      signInWithPassword,
      signOut,
      user: auth?.user || null,
    }),
    [auth, signInWithOtp, signInWithPassword, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
